/**
 * Where a pop-out menu goes.
 *
 * Separated from the component because it is arithmetic, and arithmetic can
 * be checked — the bug that produced it could not have been found by reading
 * the JSX. A menu hung off a to-do near the bottom of the screen opened
 * downwards as always, ran off the bottom edge, and the options past the fold
 * were simply unreachable: no scrollbar, because the panel had not run out of
 * room, the *window* had.
 *
 * Two rules fix that and are what the checks hold to:
 *
 *  1. The panel never leaves the viewport on any side.
 *  2. It goes below the button when there is room and above when there is
 *     not — and whichever it picks, its height is capped to the space
 *     actually available, so what does not fit scrolls inside the panel
 *     rather than off the screen.
 */

export interface Anchor {
  /** The button's rectangle, in viewport coordinates. */
  top: number;
  bottom: number;
  right: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface MenuBox {
  left: number;
  top: number;
  maxHeight: number;
  /** Which side of the button it ended up on. Handy for a shadow or an arrow. */
  above: boolean;
}

/** Breathing room from the button and from the edge of the screen. */
const GAP = 6;
const MARGIN = 8;

/**
 * Below a certain height a menu is not usable, it is a hint that one exists.
 * If neither side can offer this much, the bigger side wins and it scrolls.
 */
const USABLE = 140;

export function placeMenu(
  anchor: Anchor,
  viewport: Viewport,
  width: number,
  /** How tall the panel wants to be. Its natural height, once rendered. */
  wanted = Infinity,
): MenuBox {
  // Right-aligned to the button, and never off either side.
  const left = Math.max(MARGIN, Math.min(anchor.right - width, viewport.width - width - MARGIN));

  const roomBelow = viewport.height - anchor.bottom - GAP - MARGIN;
  const roomAbove = anchor.top - GAP - MARGIN;

  /*
   * Below unless below cannot hold it and above can hold more. Preferring
   * below keeps the common case — a menu at the top of a page — exactly where
   * it has always been, so this only changes the behaviour that was broken.
   */
  const fitsBelow = wanted <= roomBelow;
  const above = !fitsBelow && roomAbove > roomBelow && (roomAbove >= USABLE || roomAbove > roomBelow);

  const maxHeight = Math.max(0, above ? roomAbove : roomBelow);
  const height = Math.min(wanted, maxHeight);

  return {
    left,
    top: above ? Math.max(MARGIN, anchor.top - GAP - height) : anchor.bottom + GAP,
    maxHeight,
    above,
  };
}
